import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useUser } from './UserProvider';
import { getQuizResultsByUserId } from '../services/quizApi';
import { courseModules } from '../data/courseModules';

const QuizSummaryContext = createContext();

export const useQuizSummary = () => useContext(QuizSummaryContext);

export const QuizSummaryProvider = ({ children }) => {
  const { user } = useUser();
  const [summary, setSummary] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    correctAnswers: 0,
    pendingQuestions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummaryData = useCallback(async () => {
    if (!user) {
        // If there's no user (e.g., logged out), reset the summary and stop.
        setSummary({
            totalQuestions: 0,
            answeredQuestions: 0,
            correctAnswers: 0,
            pendingQuestions: 0,
        });
        setLoading(false);
        return;
    };
    
    setLoading(true);
    setError(null);
    try {
      // Use local module data and fetch user results
      const modules = courseModules;
      // The user object from useUser() contains the full user profile, which has a 'userId' property.
      // We were previously using 'user.id', which was incorrect.
      const userResults = await getQuizResultsByUserId(user.userId);

      // Add detailed logging for the raw data.
      // console.log('--- Raw User Results from API ---', userResults);

      // Group results by moduleId and find the latest one in each group.
      // This is more robust than pre-sorting the entire list.
      const latestResultsMap = new Map();
      // console.log('--- Finding latest result for each module ---');
      if (Array.isArray(userResults)) {
        for (const result of userResults) {
          // Skip records with invalid data
          if (!result || !result.moduleId || !result.takenAt) {
            // console.log('Skipping invalid record:', result);
            continue;
          }

          const moduleId = String(result.moduleId);
          const existing = latestResultsMap.get(moduleId);

          // console.log(`Processing record for moduleId "${moduleId}" with score: ${result.result}, takenAt: ${result.takenAt}`);

          if (!existing) {
            // console.log(` -> No existing record for moduleId "${moduleId}". Storing this one.`);
            latestResultsMap.set(moduleId, result);
          } else if (new Date(result.takenAt) > new Date(existing.takenAt)) {
            // console.log(` -> This record is newer than the stored one (Existing takenAt: ${existing.takenAt}). Replacing.`);
            latestResultsMap.set(moduleId, result);
          } else {
            // console.log(` -> This record is older or same age as the stored one. Skipping.`);
          }
        }
      }
      const latestResults = Array.from(latestResultsMap.values());
      // console.log('--- Final list of latest results to be summed ---', latestResults);

      const totalQuestions = modules.reduce((total, module) => {
        const quizPage = module.pages?.find(p => p.pageId === 'quiz');
        const questionCount = quizPage?.quiz?.questions?.length || 0;
        return total + questionCount;
      }, 0);
      const correctAnswers = latestResults.reduce((total, result) => total + Number(result.result || 0), 0);

      // Per user feedback, "Attempted" should reflect the number of correct answers (the 'result' attribute).
      const answeredQuestions = correctAnswers;
      // Per user feedback, "Remaining" should consistently be 0.
      const pendingQuestions = 0;

      setSummary({
        totalQuestions,
        answeredQuestions,
        correctAnswers,
        pendingQuestions,
      });
    } catch (err) {
      setError('Failed to load quiz summary.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  const value = {
    summary,
    loading,
    error,
    refreshSummary: fetchSummaryData,
  };

  return (
    <QuizSummaryContext.Provider value={value}>
      {children}
    </QuizSummaryContext.Provider>
  );
};